import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { KeyboardInput } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { getLogService } from "@cervval.js/common/src/services/log.service";
import { NoBubblesEventInit } from "@cervval.js/common/src/tools/event.tools";
import { TsDate, UnixTimestamp, UnixTimestampIndeterminateInterval, add, getTodayDate, isInside, subtract, toDate } from "@cervval.js/common/src/tools/timestamps.tools";
import { CalendarOptions, CjsCalendar, DAY_CELL_HIGHLIGHT, DAY_CELL_IS_CLICKED, DAY_CELL_ROUNDED, DAY_CELL_TODAY, HTMLDateDivElement, defaultCalendarOptions } from "../calendar/calendar";

/** Parameters for DatePicker */
export interface DatePickerOptions extends CalendarOptions {

    /** Date selection parameters */
    /** Valid range intervals
     * If empty, all dates are valid
     */
    ranges: UnixTimestampIndeterminateInterval[];

    /**
     * Other month cells "select date" behavior is replaced by "change month" behavior
     */
    enableOtherMonthCellChangeMonth: boolean;

    /**
     * Enable keyboard inputs for component (up, left, right, up, enter keys input events)
     */
    enableKeyboardInput: boolean;
}

/** Parameters for DatePicker */
export const defaultDatePickerOptions: DatePickerOptions = {

    ...defaultCalendarOptions,

    /** Date selection parameters */
    /** Valid range, if empty, all dates are valid */
    ranges: [],

    /**
     * Other month cells "select date" behavior is replaced by "change month" behavior
     */
    enableOtherMonthCellChangeMonth: false,

    /**
     * Enable keyboard inputs for component (up, left, right, up, enter keys input events)
     */
    enableKeyboardInput: true
};

// Class names constants
export const DAY_CELL_SELECTABLE = "selectable";
const UNSELECTABLE = "unselectable";

@CustomElement({
    name: "cjs-date-picker",
    style: require("./datePicker.scss"),
    elementCssClasses: ["cjs-calendar"]
    // Uses the same template as calendar (extends)
})
export class CjsDatePicker extends CjsCalendar {

    @TypedWebComponentOptions(defaultDatePickerOptions)
    public override readonly options!: WebComponentOptions<DatePickerOptions>;

    /**
     * This event is emitted when week is selected
     */
    @Event(NoBubblesEventInit)
    public readonly weekValueChangeEvent!: EventHandler<{ startOfWeek: TsDate, weekNumber: number }>;

    /** The current cell clicked */
    protected _clickedCell: HTMLDateDivElement | null = null;

    /** The current week cell clicked */
    protected _weekClickedCell: HTMLDateDivElement | null = null;


    /** The current cell preselected by user keyboard input */
    protected _keyboardPreselectedCell: HTMLDateDivElement | null = null;

    constructor(options?: Partial<DatePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(options, customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** 
     * Refresh component, re-render component with options
     */
    public override refresh(): void {
        super.refresh();

        // Add keyboard input listener
        if (this.options.enableKeyboardInput) {
            document.body.addEventListener("keydown", this._onKeyboardDown, true);
        }

        if (!this.options.enableKeyboardInput) {
            document.body.removeEventListener("keydown", this._onKeyboardDown, true);
        }
    }

    /** Triggered when the element is removed from DOM */
    public disconnectedCallback(): void {
        if (this.options.enableKeyboardInput) {
            document.body.removeEventListener("keydown", this._onKeyboardDown, true);
        }
    }


    /**
     * Set value of component
     */
    public override setValue(ts: UnixTimestamp | undefined): void {
        this._value = ts;

        if (!ts) {
            this._clickedCell = null;
            this.refresh();
            return;
        }

        // If value is not in range, return
        if (this._isDateOuterSelectedBound(ts)) {
            getLogService().warn(`Could not set value ${ts}: value out of range`);
            return;
        }

        // Clean previous cell style
        this._clickedCell?.classList.remove(DAY_CELL_IS_CLICKED);

        // Update selected date & cell
        this._clickedCell = this._getCellByDate(ts);
        this._clickedCell?.classList.add(DAY_CELL_IS_CLICKED);
        this.setGridByTs(ts);
    }

    /** Init containers listeners */
    protected override _initListeners(): void {
        super._initListeners();
    }

    /** 
     * Set up days grid for DatePickerComponent
     */
    protected override _setupDaysGrid(): void {
        super._setupDaysGrid();

        const currentDate = this.currentDate;
        for (const dayCell of this.dayCells.filter(dayCell => dayCell.date.yearAndMonth === currentDate.yearAndMonth)) {
            // Add behaviors and display
            this._addCellClickListener(dayCell);
        }

    }

    /** 
     * Set up weeks grid for DatePickerComponent
     */
    protected override _setupWeeksGrid(): void {
        super._setupWeeksGrid();

        for (const weekCell of this.weekCells) {
            // Add behaviors and display
            this._addCellClickListener(weekCell as HTMLDateDivElement, true);
        }

    }

    /** On keyboard key is pressed down event */
    protected _onKeyboardDown: EventListenerObject = {
        handleEvent: (evt: KeyboardEvent): void => {
            if (this.disabled) { return; }// Don't allow keyboardDown callback if component is disabled

            // init x (-1: left / +1: right) and y (-1: down / +1: up)
            let x = 0;
            let y = 0;

            // Get origin date: select in this order: _keyboardPreselectedCell => selectedDate => currentDate
            const fromDate: TsDate = this._keyboardPreselectedCell ? this._keyboardPreselectedCell.date : this._value ?
                toDate(this._value) : this.currentDate;

            // Switch on key input
            switch (evt.key) {
                case KeyboardInput.ARROW_DOWN:
                    y = +1;
                    break;
                case KeyboardInput.ARROW_RIGHT:
                    x = +1;
                    break;
                case KeyboardInput.ARROW_UP:
                    y = -1;
                    break;
                case KeyboardInput.ARROW_LEFT:
                    x = -1;
                    break;
                case KeyboardInput.ENTER:
                    if (this._keyboardPreselectedCell) {

                        // Reset keyboard cell display
                        this._keyboardPreselectedCell.classList.remove("preselected");

                        // Simulate user click
                        this._cellClickCallback(this._keyboardPreselectedCell);
                    }
                    return;
                default:
                    return;
            }

            // Get the date to go on based on key input
            const toDateAdd: TsDate = add(fromDate, { day: x * 1 + y * 7 });

            // Add change month behavior if not destination date isn't same month and a directional key is pressed
            if (toDateAdd.yearAndMonth !== this.currentDate.yearAndMonth && (x !== 0 || y !== 0)) {
                this._changeMonthCallback(x > 0 || y > 0 ? 1 : -1); // Change month (=~ cells displayed)
            }

            // Retrieve destination cell
            const toCell: HTMLDateDivElement | null = this._getCellByDate(toDateAdd.uts);

            if (toCell) {

                // Remove the display for previous preselected cell
                if (this._keyboardPreselectedCell) { this._keyboardPreselectedCell.classList.remove("preselected"); }

                // Add display for destination cell
                toCell.classList.add("preselected");

                // Update value
                this._keyboardPreselectedCell = toCell;
            }
        }
    };

    /**
     * Create a cell of an other month than the current one
     *
     * @param dayOffset Offset of days from
     * @param monthOffset Offset of month from 
     */
    protected override _createOtherMonthCell(dayOffset: number, monthOffset: number): HTMLDateDivElement {

        // Create cell
        const dayCell = super._createOtherMonthCell(dayOffset, monthOffset);

        if (this.options.displayOtherMonthsDays) {
            // Add click behavior
            this._addCellClickListener(dayCell);
        }

        return dayCell;
    }


    /**
     * Set date cells behaviors and displays
     */
    protected override _setCellDisplay(cell: HTMLDateDivElement): void {

        // Set up border radius display
        toggleClass(cell, this.options.roundedCells, DAY_CELL_ROUNDED);

        const isDateOuterSelectedBound = this._isDateOuterSelectedBound(cell.date.uts);

        // Highlight weekend cells days
        if (this.options.highLightDays?.includes(cell.date.weekday)) {
            // Add class from highlighDays params
            cell.classList.add(DAY_CELL_HIGHLIGHT);
        }
        // Set calendar week display
        this.setCalendarWeekCellDisplay(cell);

        // Today date display
        if (cell.date.yearAndOrdinal === getTodayDate().yearAndOrdinal && this.options.displayTodayDate) {
            cell.classList.add(DAY_CELL_TODAY);
        }

        // For other month cells, add a change month behavior
        if (this._isOtherMonthCell(cell) && !isDateOuterSelectedBound && this.options.enableOtherMonthCellChangeMonth) {
            this._addChangeMonthClickListener(cell, cell.date);
        }

        // If the selectedDate is equal to cellDate, add isClicked display
        if (this._value && toDate(this._value).yearAndOrdinal === cell.date.yearAndOrdinal) {
            cell.classList.add(DAY_CELL_IS_CLICKED);
            this._clickedCell = cell;
        } else {
            cell.classList.remove(DAY_CELL_IS_CLICKED);
        }
    }

    /**
     * Add a click listener to a cell, used to switch between selected cells for component
     * @param cell Cell to set click listener
     */
    protected _addCellClickListener(cell: HTMLDateDivElement, week?: boolean): void {
        // Check if the cell can be clickable/selected
        if (this._isDateOuterSelectedBound(cell.date.uts)) {
            cell.classList.add(UNSELECTABLE);
            return;
        }
        else {
            // Add css class for hover and cursor
            cell.classList.add(DAY_CELL_SELECTABLE);
        }

        // Add click listener, toggle "clicked" class and update the selectedDate
        cell.addEventListener("click", () => {
            if (week) {
                this._weekCellClickCallback(cell);
            } else {
                this._cellClickCallback(cell);
            }
        });
    }

    /**
     * Behavior when cell is clicked by user
     */
    protected _cellClickCallback(cell: HTMLDateDivElement): void {

        if (this.disabled) { return; }// Don't allow cellClick callback if component is disabled

        // Update selected date & cell
        if (this._value && cell.date.yearAndOrdinal === toDate(this._value).yearAndOrdinal) { return; }

        if (this._clickedCell != null) {
            this._clickedCell.classList.remove(DAY_CELL_IS_CLICKED);
        } else {
            // Remove IS_CLICKED class of cells
            this.dayCells.forEach(dayCell => dayCell.classList.remove(DAY_CELL_IS_CLICKED));
        }

        if (this._weekClickedCell) {
            this._weekClickedCell.classList.remove(DAY_CELL_IS_CLICKED);
        } else {
            // Remove IS_CLICKED class of cells
            this.weekCells.forEach(dayCell => dayCell.classList.remove(DAY_CELL_IS_CLICKED));
        }

        cell.classList.add(DAY_CELL_IS_CLICKED);

        this._clickedCell = cell;
        this._value = cell.date.uts;

        this._weekClickedCell = null;
        this._weekValue = undefined;

        this.selectedDateChange();
        this._keyboardPreselectedCell = null;

    }

    /**
     * Behavior when cell is clicked by user
     */
    protected _weekCellClickCallback(cell: HTMLDateDivElement, week?: boolean): void {

        if (this.disabled) { return; }// Don't allow cellClick callback if component is disabled

        this._weekValue = { startOfWeek: cell.date, weekNumber: cell.date.weekNumber };

        if (this._clickedCell != null) {
            this._clickedCell.classList.remove(DAY_CELL_IS_CLICKED);
        } else {
            // Remove IS_CLICKED class of cells
            this.dayCells.forEach(dayCell => dayCell.classList.remove(DAY_CELL_IS_CLICKED));
        }

        if (this._weekClickedCell != null) {
            this._weekClickedCell.classList.remove(DAY_CELL_IS_CLICKED);
        } else {
            // Remove IS_CLICKED class of cells
            this.weekCells.forEach(dayCell => dayCell.classList.remove(DAY_CELL_IS_CLICKED));
        }

        cell.classList.add(DAY_CELL_IS_CLICKED);

        this._value = undefined;
        this._clickedCell = null;

        this._weekClickedCell = cell;
        this._weekValue = { startOfWeek: cell.date, weekNumber: cell.date.weekNumber };

        this.selectedDateChange();
        this._keyboardPreselectedCell = null;
    }

    /**
     * change month behavior for an element
     * @param offset Month offset, can be a number (-1/+1) or a date from a different month than current month displayed
     * FIXME above comment is not correct offset must be a number
     * @param monthYearDiv [optional] The div to set textContent 
     */
    protected override _changeMonthCallback(offset: number | TsDate, monthYearDiv?: HTMLElement | null): void {

        super._changeMonthCallback(offset, monthYearDiv);
        // Reset clickedCell
        this._clickedCell = null;
    }

    /** When selected date is updated */
    public selectedDateChange(): void {
        if (this._weekValue) {
            this.weekValueChangeEvent.emit(this._weekValue);
        } else {
            this._dispatchOnPickerValueChange();
        }
    }

    /**
     * Check if a date is outer selectStartDate and selectEndDate range
     * @param date Date to check
     */
    protected _isDateOuterSelectedBound(date: UnixTimestamp): boolean {

        // If ranges are defined, check if date is in one range
        if (this.options.ranges.length) {
            for (const range of this.options.ranges) {
                if (this._isDateInRange(date, range)) { return false; }
            }
            // Return true only if date is not in a range interval
            return true;
        }

        // If there is no range, all dates are valid
        return false;
    }

    /**
     * Return true if the given date in include in range
     * @param date date to check
     * @param range range to check in
     */
    protected _isDateInRange(date: UnixTimestamp, range: UnixTimestampIndeterminateInterval): boolean {
        return isInside(date, range.start, range.finish);
    }

    /**
     * Get a cell div element from his date value
     * @param ts value of date to get corresponding cell
     */
    protected _getCellByDate(ts: UnixTimestamp, p?: { isFinishDate: boolean }): HTMLDateDivElement | null {
        if (this.components.dayGrid) {

            // Get all calendar cells divs
            const childrens: HTMLCollection = this.components.dayGrid.children;

            for (let i = 0; i < childrens.length; i++) {
                // Check if cell date is equal to date parameter getter
                const cell: HTMLDateDivElement = childrens[i] as HTMLDateDivElement;

                let tsToRetrieve = ts;
                if (p?.isFinishDate) {// Remove one day to date to retrieve correct cell (used in dateIntervalPicker)
                    tsToRetrieve = subtract(ts, { day: 1 }).uts;
                }
                if (cell.date.uts === tsToRetrieve) {
                    return cell;
                }
            }

        }
        return null;
    }

    /** Reset date to today */
    protected override _resetTodayDate(): void {
        super._resetTodayDate();

        // Dispatch change event
        this.selectedDateChange();
    }
}